import { DefaultContext } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  from,
  map,
  merge,
  shareReplay,
  skipWhile,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs'

import { OnboardRequestInput, RequestOnboardDocument } from '../../graphql/search'
import { isNotNull } from '../../utils/collectionTools'
import { logAndCaptureException } from '../../utils/errorTools'
import { setAnalyticsEvent } from '../analytics/state'
import { refreshAccessToken } from '../auth/expiration'
import { latestAuthStateWithAccessToken$ } from '../auth/state'
import { latestCommaClient$ } from '../graphql/apollo'
import { setOnboardingRequestState } from './state/request-state'
import { getActiveOnboarding } from './utils'

const [onboardingInput$, setOnboardingInput] = createSignal<OnboardRequestInput>()

// create hook for sharing latest input pending subscription to request observable
const [useLatestOnboardingInput, latestOnboardingInput$] = bind(onboardingInput$, null)

const retryOnboardRequest$ = new Subject<void>()

export { setOnboardingInput, useLatestOnboardingInput }

export const retryOnboardRequest = () => {
  retryOnboardRequest$.next()
}

const createOnboardRequest$ = () => {
  return combineLatest([
    latestCommaClient$,
    latestOnboardingInput$.pipe(filter(isNotNull)),
    latestAuthStateWithAccessToken$.pipe(filter(isNotNull)),
  ]).pipe(
    skipWhile(([_, __, authState]) => {
      if (!authState.isAuthenticated) return true
      const activeOnboarding = getActiveOnboarding(authState.user)
      return !activeOnboarding?.claim
    }),
    take(1),
    tap(() => {
      setOnboardingRequestState({ name: 'signup_loading' })
    }),
    switchMap(([client, input, authState]) => {
      if (!authState.isAuthenticated) {
        return EMPTY
      }

      return from(
        client.mutate({
          mutation: RequestOnboardDocument,
          variables: {
            input,
          },
          context: { token: authState.accessToken } as DefaultContext,
        }),
      ).pipe(
        map((response) => {
          if ('errors' in response && response.errors && response.errors.length > 0) {
            const errorMessage =
              response.errors[0]?.message || 'Onboarding failed. Please try again.'
            throw new Error(errorMessage)
          }
          return { input, response }
        }),
        catchError((error) => {
          logAndCaptureException(error)
          setOnboardingRequestState({ name: 'signup_error', error })
          return EMPTY
        }),
        switchMap((result) =>
          refreshAccessToken().pipe(
            map(() => result), // Preserve the original result after token refresh
          ),
        ),
        tap({
          next: (result) => {
            setOnboardingRequestState({
              name: 'signup_complete',
              response: { success: true, user: result },
            })

            const userType = Object.keys(result.input)[0] // Determine user type based on input key
            setAnalyticsEvent({
              event: 'sign_up',
              user_type: userType,
            })
          },
        }),
      )
    }),
  )
}

let hasInitialRequest = false

export const onboardRequest$ = merge(
  latestOnboardingInput$.pipe(
    filter(isNotNull),
    filter(() => {
      if (!hasInitialRequest) {
        hasInitialRequest = true
        return true
      }
      return false
    }),
  ),
  retryOnboardRequest$,
).pipe(
  switchMap(() => createOnboardRequest$()),
  shareReplay(1),
)
